import '@signal24/vue-foundation/dist/vue-foundation.css';
import './modules/mobile/helper/bridge';
import './otel';
import './shared/services/sse';

import Tracker from '@openreplay/tracker';
import TrackerAssist from '@openreplay/tracker-assist';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import App from './app.vue';
import { configureApiClients } from './openapi-clients/shared';
import router from './router';
import { registerAnimationDirective } from './shared/directives/animation.directive';
import { registerNextFocusDirective } from './shared/directives/nextfocus.directive';
import { hasPermission } from './shared/helpers/permissions.helpers';
import { OpenReplay } from './shared/services/openreplay';
import { setupVf } from './vf.setup';

// lazy (because it's a function call)
import('@fortawesome/fontawesome-pro/css/all.css');
import('@vuepic/vue-datepicker/dist/main.css');

configureApiClients();

const app = createApp(App);

if (import.meta.env.VITE_APP_SENTRY_DSN) {
    const matches = import.meta.env.VITE_APP_SENTRY_DSN.match(/\/\/(.+)@.+\/(\d+)$/);
    if (matches) {
        Sentry.init({
            app: app,
            dsn: import.meta.env.VITE_APP_SENTRY_DSN,
            environment: import.meta.env.MODE,
            release: `zynosuite-spa@${import.meta.env.MODE}-${import.meta.env.VITE_APP_VERSION}`,
            tunnel: `https://sentry.s24.dev/proxy/${matches[2]}/${matches[1]}`,
            beforeBreadcrumb(breadcrumb) {
                const url = breadcrumb.category === 'fetch' ? breadcrumb.data?.url : null;
                return url?.includes('openreplay.s24.io') || url?.includes('s24.dev') ? null : breadcrumb;
            }
        });
    }
}

if (import.meta.env.VITE_APP_OPENREPLAY_URL && import.meta.env.VITE_APP_OPENREPLAY_KEY) {
    OpenReplay.tracker = new Tracker({
        __DISABLE_SECURE_MODE: import.meta.env.MODE === 'development',
        ingestPoint: import.meta.env.VITE_APP_OPENREPLAY_URL,
        projectKey: import.meta.env.VITE_APP_OPENREPLAY_KEY,
        network: {
            ignoreHeaders: ['authorization'],
            capturePayload: true,
            sessionTokenHeader: false,
            captureInIframes: true,
            failuresOnly: false,
            sanitizer(data) {
                // we don't need payloads for non-error responses
                if (data.status < 400) {
                    data.request.body = null;
                    data.response.body = null;
                }
                return data;
            }
        },
        obscureTextEmails: false,
        obscureInputEmails: false,
        defaultInputMode: 0 // plain
    });
    OpenReplay.tracker.use(TrackerAssist({}));
    OpenReplay.start();
}

setupVf(app, {
    $hp: hasPermission
});

const pinia = createPinia();
app.use(pinia);
app.use(router);

registerAnimationDirective(app);
registerNextFocusDirective(app);

app.mount(document.body);

new Date(); // BS to force a new bundle hash to cache bust
